import React, { useEffect, useRef, useState } from 'react';
import { archive } from '../data/archiveData';
import './Archive.css';

function ArchiveImage({ item, thumbnail = false }) {
  const [width, height] = item.size;
  const [x, y, w, h] = (thumbnail && item.thumbnailCrop) || item.crop || [0, 0, width, height];
  return <span className="archive-image" style={{ '--image-ratio': w / h, aspectRatio: `${w} / ${h}` }}>
    <img src={item.src} alt={thumbnail ? '' : item.alt} loading={thumbnail ? 'lazy' : 'eager'}
      decoding="async" width={width} height={height}
      style={{ width: `${width / w * 100}%`, left: `${-x / w * 100}%`, top: `${-y / h * 100}%` }} />
  </span>;
}

export default function Archive({ project = null, photoRequest = null }) {
  const [filter, setFilter] = useState(project || 'All');
  const [selected, setSelected] = useState(null);
  const railRef = useRef(null);
  const dialogRef = useRef(null);
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const collection = project ? archive.filter((frame) => frame.project === project) : archive;
  const visible = collection.filter((item) => filter === 'All' || item.project === filter);
  const item = selected === null ? null : visible[selected];
  const open = selected !== null;
  const move = (direction) => setSelected((current) => (current + direction + visible.length) % visible.length);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!open) { if (dialog.open) dialog.close(); return undefined; }
    dialog.showModal();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = overflow; if (dialog.open) dialog.close(); };
  }, [open]);

  useEffect(() => {
    if (!photoRequest) return;
    setFilter(photoRequest.project);
    setSelected(null);
    headingRef.current.focus({ preventScroll: true });
    sectionRef.current.scrollIntoView({
      block: 'start',
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
    railRef.current.scrollTo({ left: 0, behavior: 'instant' });
  }, [photoRequest]);

  useEffect(() => { railRef.current.scrollTo({ left: 0, behavior: 'instant' }); }, [filter]);

  const scroll = (direction) => railRef.current.scrollBy({
    left: direction * railRef.current.clientWidth * .8,
    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
  });

  return <section ref={sectionRef} id="project-archive" className="archive" aria-labelledby="archive-title">
    <div className="archive-heading">
      <div><p className="chain-eyebrow">From the archive / {String(visible.length).padStart(3, '0')} frames</p>
        <h2 ref={headingRef} tabIndex={-1} id="archive-title">Notes, prototypes & small wins.</h2></div>
      <div className="archive-tools">
        {!project && <div className="archive-filters" role="group" aria-label="Filter archive">
          {['All', 'Evrald', 'Dhow', 'Knewit', 'Mantle'].map((project) => <button key={project} aria-pressed={filter === project}
            onClick={() => setFilter(project)}>{project}</button>)}
        </div>}
        <div className="chain-arrows">
          <button aria-label="Scroll archive left" onClick={() => scroll(-1)}>←</button>
          <button aria-label="Scroll archive right" onClick={() => scroll(1)}>→</button>
        </div>
      </div>
    </div>
    <div className="archive-rail" ref={railRef} role="list" aria-label="Project archive">
      {visible.map((frame, index) => {
        const Element = frame.href ? 'a' : 'button';
        return <div className="archive-frame" key={frame.id} role="listitem">
          <Element className="archive-open" aria-label={`${frame.href ? 'Watch' : 'View'} ${frame.project}: ${frame.title}`}
            href={frame.href} target={frame.href ? '_blank' : undefined} rel={frame.href ? 'noreferrer' : undefined}
            onClick={frame.href ? undefined : () => setSelected(index)}>
            {frame.video ? <span className="archive-thumb archive-video">
              <span className="archive-video-label">KNEWIT / VIDEO</span>
              <span className="archive-video-title">Can AI predict<br />the Challenger Cup?</span>
              <span className="archive-video-play">▶ Watch on Bilibili ↗</span>
            </span> : <span className="archive-thumb">
              <ArchiveImage item={frame} thumbnail /><span className="archive-enlarge" aria-hidden="true">↗</span></span>}
            <span className="archive-frame-meta"><span>{frame.project}</span><span>{String(archive.indexOf(frame) + 1).padStart(2, '0')}</span></span>
            <span className="archive-frame-title">{frame.title}</span>
          </Element>
        </div>;
      })}
    </div>
    <dialog ref={dialogRef} className="archive-dialog"
      style={item && !item.video ? { '--lightbox-width': `calc(60svh * ${(item.crop?.[2] || item.size[0]) / (item.crop?.[3] || item.size[1])} + 50px)` } : undefined} aria-labelledby="archive-detail-title" aria-describedby="archive-detail-caption"
      onCancel={(event) => { event.preventDefault(); setSelected(null); }}
      onClick={(event) => { if (event.target === event.currentTarget) setSelected(null); }}
      onKeyDown={(event) => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1); }
      }}>
      {item && <div className="archive-dialog-inner">
        <div className="archive-dialog-top"><span>{item.project} / {item.kind}</span>
          <button className="archive-close" onClick={() => setSelected(null)} aria-label="Close photo">Close ×</button></div>
        <div className="archive-stage">{item.video ? <a className="archive-video-detail" href={item.href} target="_blank" rel="noreferrer">▶ Watch on Bilibili ↗</a> : <ArchiveImage item={item} />}</div>
        <div className="archive-dialog-bottom"><div aria-live="polite">
          <h3 id="archive-detail-title">{item.title}</h3><p id="archive-detail-caption">{item.caption}</p>
          {item.sourceHref && <a className="archive-source-link" href={item.sourceHref} target="_blank" rel="noreferrer">{item.sourceLabel} ↗</a>}
        </div><div className="archive-paging"><span>{String(selected + 1).padStart(2, '0')} / {String(visible.length).padStart(2, '0')}</span>
          <div className="chain-arrows"><button onClick={() => move(-1)} aria-label="Previous photo">←</button>
            <button onClick={() => move(1)} aria-label="Next photo">→</button></div>
        </div></div>
      </div>}
    </dialog>
  </section>;
}
